import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, message, Tooltip, Select, Spin } from 'antd';
import { EditOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import {
  
  updateCategoryService,
  deleteCategoryService,
  fetchCategoriesService,
  fetchSubCategoriesService,
  createSubCategoryService,
  updateSubCategoryService
} from '../../services/CategoryService';

const { confirm } = Modal;
const { Option } = Select;

const SubCategoryTable = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formLoading, setFormLoading] = useState(false);  // To handle form submit loading
  const [tableLoading, setTableLoading] = useState(false); // To handle table loading
  const [categories, setCategories] = useState([]);
  const [Scategories, setSCategories] = useState([]);
  const [form] = Form.useForm();

  // Fetch categories whenever the component is mounted or refreshed
  const fetchCategories = async () => {
    setTableLoading(true);
    try {
      const data = await fetchCategoriesService();
      setCategories(data);
      const dataS = await fetchSubCategoriesService();
      setSCategories(dataS);
    } catch (error) {
      message.error('Error fetching categories');
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []); // Empty dependency array, only fetch once when component mounts

  const showDeleteConfirm = (id) => {
    confirm({
      title: 'Are you sure you want to delete this Sub-category?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          await deleteCategoryService(id);
          message.success('Sub-Category deleted successfully');
          fetchCategories(); // Refetch categories after delete
        } catch (error) {
          message.error('Error deleting Sub-category');
        }
      },
      onCancel() {
        console.log('Delete canceled');
      },
    });
  };

  // Handle creating or updating category
  const handleSubmit = async (values) => {
    setFormLoading(true); // Enable loading when form is submitted
    try {
      const { name, parentId } = values;
      console.log(values);
      if (editingCategory) {
        // Update existing category
        await updateSubCategoryService(editingCategory.id,  name, parentId );
        message.success('Category updated successfully');
      } else {
        // Create new category
        await createSubCategoryService( name, parentId );
        await fetchCategories(); // Refetch categories after create/update
        message.success('Sub-Category created successfully');
      }
      setIsModalVisible(false);
      form.resetFields();
      
    } catch (error) {
      message.error('Error creating/updating category');
    } finally {
      setFormLoading(false);
    }
  };

  // Show modal for editing category
  const showEditModal = (category) => {
    console.log(category);
    setEditingCategory(category);
    form.setFieldsValue({ name: category.name, parentId: category.parentId || null });
    setIsModalVisible(true);
  };

  // Show modal for creating new category
  const showCreateModal = () => {
    setEditingCategory(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Parent Category',
      dataIndex: ['parent', 'name'], // Nested access to parent name directly
      key: 'parentId',
      render: (parentName) => {
        return parentName ? parentName : 'None'; // If parent name exists, render it; otherwise, show 'None'
      },
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => new Date(text).toLocaleString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (text, record) => (
        <>
          <Tooltip title="Edit">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => showEditModal(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="link"
              icon={<DeleteOutlined />}
              danger
              onClick={() => showDeleteConfirm(record.id)}
            />
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <>
      <Button type="primary" onClick={showCreateModal} style={{ marginBottom: 16 }}>
        Create new Sub-Category
      </Button>

      <Spin spinning={tableLoading}>
        <Table columns={columns} dataSource={Scategories} rowKey="id" />
      </Spin>

      <Modal
        title={editingCategory ? 'Edit Sub-Category' : 'Create Sub-Category'}
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={formLoading} // Show loading on the OK button
      >
        <Form form={form} onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label=" Name"
            rules={[{ required: true, message: 'Please input the category name!' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="parentId"
            label="Parent Category"
          >
            <Select placeholder="Select parent category (optional)">
              <Option value={null}>None</Option>
              {categories
                .filter((cat) => !editingCategory || cat.id !== editingCategory.id)
                .map((cat) => (
                  <Option key={cat.id} value={cat.id}>
                    {cat.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default SubCategoryTable;